import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { AvForm } from 'availity-reactstrap-validation';
import SaveSubmitButton from '../../form/buttons/SaveSubmitButton';
import { closeModalSafely, handleSubmit } from './create-or-edit-table.service';
import ModalSheet from '../modal-sheet/modal-sheet.component';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { setSpecificTable } from '../../../../redux/store/store.actions';
import FormField from '../../form/fields/FormField';
import {
  setCreateTableModalData,
  setIsCreateTableModalOpen,
  setIsEditTableModalOpen,
} from '../../../../redux/modal/modal.actions';
import { Col, Row } from 'reactstrap';
import DefaultAlertComponent from '../../alerts/default-alert/default-alert.component';
import PosIntegrationRefField from '../../form/fields/PosIntegrationRefField';
import LabeledToggle from '../../form/LabeledToggle';
import TableField from '../../form/fields/TableField';
import DropdownField from '../../form/fields/DropdownField';
import { fetchAreas } from '../../../../redux/store/store.actions';

const CreateOrEditTableModal = ({
  isCreateTableModalOpen,
  isEditTableModalOpen,
  createTableModalData,
  editTableModalData,
  areas,

  setIsCreateTableModalOpen,
  setIsEditTableModalOpen,
  setCreateTableModalData,
  setSpecificTable,
  store,
  fetchAreas,
}) => {
  const intl = useIntl();
  const [targetTable, setTargetTable] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const bulkStartingNumberInput = useRef(null);

  const isOpen = isCreateTableModalOpen || isEditTableModalOpen;
  const isEditMode = isEditTableModalOpen;
  const { table } = (isEditMode ? editTableModalData : null) || {};
  const { createMultipleTables } =
    (isCreateTableModalOpen ? createTableModalData : null) || {};
  const { posIntegration, enableTableReservations } =
    store?.merchantProfile || {};
  const formRef = useRef(null);

  const closeModal = () => {
    isCreateTableModalOpen
      ? setIsCreateTableModalOpen(false)
      : setIsEditTableModalOpen(false);
    setCreateTableModalData(null);
  };
  useEffect(() => {
    if (isOpen) {
      setTargetTable((isEditMode && table) || {});
    } else {
      // reset modal on closing
      setTargetTable({});
    }
  }, [table, isEditMode, isOpen]);

  const _closeModalSafely = () => closeModalSafely(isSubmitting, closeModal);
  const _handleSubmit = (event, errors, values) => {
    handleSubmit(
      event,
      errors,
      values,
      targetTable,
      isEditMode,
      setIsSubmitting,
      setTargetTable,
      closeModal,
      (table) => {
        fetchAreas();
        setSpecificTable(table);
      },
      createMultipleTables,
    );
  };

  return (
    <ModalSheet
      isOpen={isOpen}
      closeModal={_closeModalSafely}
      title={intl.formatMessage({
        id: isEditMode
          ? 'editTableTitle'
          : createMultipleTables
          ? 'createMultipleTablesPlaceHolder'
          : 'createNewTableTitle',
      })}
      footer={
        <div className="align-content-center">
          <SaveSubmitButton
            data-testid="saveTableButton"
            className="m-auto"
            disabled={isSubmitting}
            isSubmitting={isSubmitting}
            onClick={() => formRef?.current.handleSubmit()}
          />
        </div>
      }
    >
      <div className="px-1">
        {createMultipleTables && (
          <DefaultAlertComponent
            className="success"
            msg={intl.formatMessage({ id: 'createMultipleTablesText' })}
          />
        )}
        <AvForm
          onSubmit={_handleSubmit}
          disabled={isSubmitting}
          className="mt-2"
          ref={formRef}
        >
          <Row data-testid="tableRowModal">
            <Col xs={6} className="pr-50">
              <TableField
                data-testid="nameInputField"
                propertyName="name"
                messageId="name"
                groupClassName={
                  !posIntegration && !createMultipleTables && 'mb-1'
                }
                object={targetTable}
                isMultiple={createMultipleTables}
                required={!createMultipleTables}
              />
            </Col>

            <Col xs={6} className="pl-50">
              <FormField
                data-testid="capacityInputField"
                type="text"
                propertyName="capacity"
                messageId="tableCapacityLabel"
                groupClassName={
                  !posIntegration && !createMultipleTables && 'mb-1'
                }
                object={targetTable}
                // hint: validate gets the value from valueParser and validates it then
                validate={{ number: true }}
                valueParser={(value) => parseInt(value) || {}} // {} is definitely not a number
                required
              />
            </Col>
          </Row>

          {createMultipleTables && (
            <Row className="mb-50">
              <Col xs={6} className="pr-50">
                <FormField
                  data-testid="multipleStartNumber"
                  ref={bulkStartingNumberInput}
                  type="number"
                  propertyName="startingNumber"
                  messageId="placeholderRangeStart"
                  groupClassName="mb-1"
                  object={targetTable}
                  required
                />
              </Col>
              <Col xs={6} className="pl-50">
                <FormField
                  data-testid="multipleEndNumber"
                  type="number"
                  propertyName="endingNumber"
                  messageId="placeholderRangeEnd"
                  groupClassName="mb-1"
                  object={targetTable}
                  // hint: validate gets the value from valueParser and validates it then
                  validate={{ number: true }}
                  valueParser={(value) => {
                    const startingNumberValue = document.getElementById(
                      'startingNumber',
                    )?.value;
                    const startingNumber = parseInt(startingNumberValue);
                    const endingNumber = parseInt(value);
                    return startingNumber < endingNumber ? endingNumber : {};
                  }}
                  required
                />
              </Col>
            </Row>
          )}
          <DropdownField
            isClearable={true}
            isMulti={false}
            options={areas || []}
            propertyName="areaId"
            labelMessageId="selectAreaLabel"
            getOptionValue={(area) => area?.id}
            values={targetTable?.area?.id}
            emptyValue={null}
            getOptionLabel={(area) => area?.name}
          />
          {!createMultipleTables &&
            posIntegration &&
            ([
              'HelloTessPosIntegration',
              'DeliverectPosIntegration',
              'MatrixPosIntegration',
            ].includes(posIntegration.type) ? (
              <PosIntegrationRefField
                placeholder={intl.formatMessage({
                  id: 'selectPosIntegrationRefLabel',
                })}
                data-testid="posIntegrationRefHelloTess"
                type="table"
                propertyName="posIntegrationRef"
                groupClassName="mb-1"
                object={targetTable}
                setter={setTargetTable}
              />
            ) : (
              <FormField
                data-testid="posIntegrationRef"
                propertyName="posIntegrationRef"
                messageId="selectPosIntegrationRefLabel"
                groupClassName="mb-1"
                object={targetTable}
                required
              />
            ))}
          {enableTableReservations && (
            <LabeledToggle
              disabled={isSubmitting}
              checked={targetTable?.enableReservations}
              onClick={() =>
                setTargetTable({
                  ...targetTable,
                  enableReservations: !targetTable?.enableReservations,
                })
              }
              wrapperClassName="d-flex align-content-center justify-content-between mb-1"
              headingMessageId="enableReservationsHeading"
              subHeadingMessageId="enableReservationsSubHeading"
            />
          )}

          <LabeledToggle
            disabled={isSubmitting}
            checked={targetTable?.isActive}
            onClick={() =>
              setTargetTable({
                ...targetTable,
                isActive: !targetTable?.isActive,
              })
            }
            wrapperClassName="d-flex align-content-center justify-content-between mt-25"
            headingMessageId="lockTableLabel"
            headingClassName="d-block dark"
          />
        </AvForm>
      </div>
    </ModalSheet>
  );
};

CreateOrEditTableModal.propTypes = {
  isCreateTableModalOpen: PropTypes.bool,
  isEditTableModalOpen: PropTypes.bool,
  createTableModalData: PropTypes.object,
  editTableModalData: PropTypes.object,
  setIsCreateTableModalOpen: PropTypes.func,
  setIsEditTableModalOpen: PropTypes.func,
};

const mapStateToProps = (state) => ({
  isCreateTableModalOpen: state.modals.isCreateTableModalOpen,
  isEditTableModalOpen: state.modals.isEditTableModalOpen,
  createTableModalData: state.modals.createTableModalData,
  editTableModalData: state.modals.editTableModalData,
  store: state.store.store,
  areas: state.store?.areas,
});

export default connect(mapStateToProps, {
  setIsCreateTableModalOpen,
  setIsEditTableModalOpen,
  setSpecificTable,
  setCreateTableModalData,
  fetchAreas,
})(CreateOrEditTableModal);
